import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdCheckmark } from "react-icons/io";
import { twMerge } from "tailwind-merge";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { useSearchParams } from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import { CurrencyType, currencySymbolMapper } from "../../../../utils/currency";

type CreditKind = "sms" | "whatsapp";

interface AddCreditModalProps {
  show: boolean;
  onClose: () => void;
  kind?: CreditKind;
}

interface PricingPackage {
  price: number;
  smsQuantity: number;
}

interface DisplayPackage {
  price: number;
  quantity: number;
}

export const PRICING_PACKAGES: PricingPackage[] = [
  { price: 20, smsQuantity: 900 },
  { price: 50, smsQuantity: 2500 },
  { price: 100, smsQuantity: 5500 },
  { price: 200, smsQuantity: 12000 },
  { price: 500, smsQuantity: 33000 },
  { price: 1000, smsQuantity: 70000 },
];

const WHATSAPP_PRICING_PACKAGES: DisplayPackage[] = [
  { price: 20, quantity: 180 },
  { price: 50, quantity: 450 },
  { price: 100, quantity: 900 },
  { price: 200, quantity: 1800 },
  { price: 500, quantity: 4500 },
  { price: 1000, quantity: 9000 },
];

const CREDIT_PLAN_ID: Record<CreditKind, string> = {
  sms: "SUMOC_SMSGTO20USD",
  whatsapp: "SUMOC_WAGTO20USD",
};

const CURRENCIES: CurrencyType[] = ["USD", "EUR", "CHF"];

export function AddCreditModal({
  show,
  onClose,
  kind = "sms",
}: AddCreditModalProps) {
  const isWhatsapp = kind === "whatsapp";
  const displayPackages: DisplayPackage[] = isWhatsapp
    ? WHATSAPP_PRICING_PACKAGES
    : PRICING_PACKAGES.map((p) => ({
        price: p.price,
        quantity: p.smsQuantity,
      }));
  const { t } = useTranslation();
  const { user } = useAuth();

  const [searchParams, SetUrlSearchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState<DisplayPackage | null>(
    null
  );
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>("USD");

  const localeBaseCurrencyMapper = useCallback((postfix: string) => {
    const mapper: { [key: string]: CurrencyType } = {
      EU: "EUR",
      US: "USD",
      FR: "EUR",
      CH: "CHF",
    };

    return mapper[postfix];
  }, []);

  useEffect(() => {
    if (
      searchParams.has("language") &&
      !searchParams.has("currency") &&
      ["US", "EU", "FR", "CH"].includes(
        searchParams.get("language")?.split("-").pop() as string
      )
    )
      setSelectedCurrency(
        localeBaseCurrencyMapper(
          searchParams.get("language")?.split("-").pop() as string
        )
      );

    if (
      searchParams.has("currency") &&
      ["USD", "EUR", "CHF"].includes(searchParams.get("currency") as string)
    )
      setSelectedCurrency(searchParams.get("currency") as CurrencyType);
  }, [localeBaseCurrencyMapper, searchParams]);

  const handleProceedToPayment = async () => {
    if (!selectedPackage) return;

    const planIdentification = CREDIT_PLAN_ID[kind];
    window.location.href = `${
      process.env.REACT_APP_PAYMENT_SERVICE
    }/payment/charge?template_image_url=https://cdn.1cdn.app/application/UMD/2405110558455_logo.png&redirect_page=${
      process.env.REACT_APP_HOST_URL
    }/control-panel/credit/result&user_identification=${
      user.user_identification
    }&payment_plan_identification=${planIdentification}&language_code=en&entity_identification=${
      user.entity_identification
    }&amount=${selectedPackage.price * 100}&currency=${selectedCurrency}`;
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const formatPrice = (price: number) => {
    const symbol = currencySymbolMapper[selectedCurrency];
    if (selectedCurrency === "CHF") {
      return `${symbol} ${formatNumber(price)}`;
    }
    return `${symbol}${formatNumber(price)}`;
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="2xl"
      title={t(
        isWhatsapp
          ? "dashboard.entities.viewEntityUser.addCreditModal.whatsappTitle"
          : "dashboard.entities.viewEntityUser.addCreditModal.title"
      )}
      subtitle={t(
        isWhatsapp
          ? "dashboard.entities.viewEntityUser.addCreditModal.whatsappInfoBanner"
          : "dashboard.entities.viewEntityUser.addCreditModal.infoBanner"
      )}
      footer={
        <>
          <CustomButton
            variant="secondary"
            onClick={onClose}
            title={t("common.cancel") as string}
          />
          <CustomButton
            variant="primary"
            onClick={handleProceedToPayment}
            disabled={!selectedPackage}
            title={
              t(
                "dashboard.entities.viewEntityUser.addCreditModal.proceedToPayment"
              ) as string
            }
          />
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3">
          <h4 className="text-lg font-semibold text-gray-900">
            {t(
              isWhatsapp
                ? "dashboard.entities.viewEntityUser.addCreditModal.whatsappChoosePackage"
                : "dashboard.entities.viewEntityUser.addCreditModal.choosePackage"
            )}
          </h4>
          <div
            role="group"
            aria-label={
              t("customRegistration.payment.paymentCurrencyLabel") as string
            }
            className="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1"
          >
            {CURRENCIES.map((currency) => {
              const active = selectedCurrency === currency;
              return (
                <button
                  key={currency}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSelectedCurrency(currency);
                    if (searchParams.has("currency"))
                      SetUrlSearchParams({ currency });
                  }}
                  className={twMerge(
                    "rounded-md px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                    active
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  )}
                >
                  {currency}
                </button>
              );
            })}
          </div>
        </div>

        <div
          role="radiogroup"
          aria-label={
            t(
              "dashboard.entities.viewEntityUser.addCreditModal.choosePackage"
            ) as string
          }
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {displayPackages.map((pkg) => {
            const isSelected = selectedPackage?.price === pkg.price;

            return (
              <button
                key={pkg.price}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelectedPackage(pkg)}
                className={twMerge(
                  "flex flex-col rounded-lg border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
                  isSelected
                    ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                    : "border-gray-200 bg-white hover:border-gray-300"
                )}
              >
                <div className="mb-2 flex h-5 items-center justify-between gap-2">
                  {pkg.price === 100 ? (
                    <StatusBadge
                      color="blue"
                      size="xs"
                      className="shrink-0 whitespace-nowrap"
                    >
                      {t(
                        "dashboard.entities.viewEntityUser.addCreditModal.mostPopular"
                      )}
                    </StatusBadge>
                  ) : !isWhatsapp && pkg.price === 1000 ? (
                    <StatusBadge
                      color="success"
                      size="xs"
                      className="shrink-0 whitespace-nowrap"
                    >
                      {t(
                        "dashboard.entities.viewEntityUser.addCreditModal.bestValue"
                      )}
                    </StatusBadge>
                  ) : (
                    <span />
                  )}
                  {isSelected && (
                    <IoMdCheckmark
                      aria-hidden
                      className="h-5 w-5 shrink-0 text-blue-600"
                    />
                  )}
                </div>
                <div className="text-2xl font-semibold leading-none tabular-nums text-gray-900">
                  {formatNumber(pkg.quantity)}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {t(
                    "dashboard.entities.viewEntityUser.addCreditModal.creditsUnit"
                  )}
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-2 border-t border-gray-100 pt-3">
                  <span className="text-xs text-gray-500">
                    {t(
                      "dashboard.entities.viewEntityUser.addCreditModal.totalCost"
                    )}
                  </span>
                  <span className="text-base font-semibold tabular-nums text-gray-900">
                    {formatPrice(pkg.price)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-500">
          {t("dashboard.entities.viewEntityUser.addCreditModal.taxNote")}
        </p>
      </div>
    </ModalShell>
  );
}
